import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { fromJS } from 'immutable';

import {
    fetchChannelMembers,
    updateChannelMember,
} from 'flow/actions/channels-actions';
import { fetchExtensions } from 'flow/actions/extension-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ChannelMemberDTO } from 'flow/types/Channel';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { contactNameSorter } from 'flow/utils/sorter-utils';
import { twMerge } from 'tailwind-merge';

const PbxLiveChatMember = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const { channelKey } = useParams<PbxParams>();

    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const channelMembers = useAppSelector((state) =>
        state.channels.getIn(['channels', channelKey, 'channelMemberDTOs']),
    );
    const editable = useAppSelector((state) =>
        state.channels.getIn(['channels', channelKey, 'editable']),
    );
    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    useEffect(() => {
        dispatch(fetchExtensions('contact'));
        dispatch(fetchChannelMembers(channelKey));
    }, [dispatch, channelKey]);

    if (!channelMembers) {
        return <Loading />;
    }

    const getContact = (channelMember: TypedMap<ChannelMemberDTO>) =>
        channelMember.get('contactDTO') ||
        extensions
            .find(
                (extension) =>
                    extension.get('chatUserKey') ===
                    channelMember.get('chatUserKey'),
            )
            ?.get('contact');

    return (
        <Page
            header={i18next.t('Queue.HandleMembers.Title')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.channels.entityKey'], {
                        entityKey: channelKey,
                    }),
                )
            }
            action={
                editable && ownExtension.get('admin') ? (
                    <IconButton
                        onClick={() =>
                            openModal('addLiveChatMember', { channelKey })
                        }
                        tooltipLabel={i18next.t('chatsettings.add_member')}
                    >
                        <Icon iconKey="person_add" />
                    </IconButton>
                ) : null
            }
        >
            {channelMembers
                .sortBy(getContact, contactNameSorter)
                .map((channelMember) => {
                    const toggleDisabled =
                        !channelMember.get('editable') ||
                        (!ownExtension.get('admin') &&
                            ownExtensionKey !==
                                channelMember.get('extensionKey'));

                    return (
                        <SettingRow
                            key={`channel-member-${channelMember.get('key')}`}
                            cta={
                                <div
                                    key={`togglekey-${channelMember.get('key')}`}
                                    className="flex items-center gap-2"
                                >
                                    <Toggle
                                        value={channelMember.get('loggedIn')}
                                        disabled={toggleDisabled}
                                        onChange={(loggedIn: boolean) =>
                                            dispatch(
                                                updateChannelMember(
                                                    channelKey,
                                                    channelMember.get('key'),
                                                    fromJS({
                                                        key: channelMember.get(
                                                            'key',
                                                        ),
                                                        loggedIn,
                                                    }),
                                                ),
                                            )
                                        }
                                        activeColor="bg-pbx-channel"
                                        text={extractName(
                                            getContact(channelMember),
                                            i18next.t('status.unknown'),
                                        )}
                                    />
                                </div>
                            }
                            className="h-12"
                        >
                            <ContactAvatar
                                contact={getContact(channelMember)}
                                fallBackBackgroundColorClass={twMerge(
                                    'group-hover:bg-gray-300 bg-gray-200',
                                )}
                                size="small"
                            />

                            <div className="truncate text-gray-800">
                                {extractName(
                                    getContact(channelMember),
                                    i18next.t('status.unknown'),
                                )}
                            </div>
                        </SettingRow>
                    );
                })}

            {!channelMembers.isEmpty() ? <Divider className="my-6" /> : null}

            {ownExtension.get('admin') ? (
                <div className="flex">
                    <Button
                        color="primary"
                        onClick={() =>
                            history.push(
                                generatePath(
                                    routes[
                                        'pbx.channels.channelKey.members.remove'
                                    ],
                                    {
                                        channelKey,
                                    },
                                ),
                            )
                        }
                    >
                        {i18next.t('pbx.edit_members')}
                    </Button>
                </div>
            ) : null}
        </Page>
    );
};

export default PbxLiveChatMember;
